import * as THREE from "three";

/**
 * PHOTOSCANNED MAP SURFACES (2026-10-23). Real CC0 Poly Haven scans replace the
 * procedural canvas families in mapTextures.ts on capable machines. Files live
 * in `public/textures/pbr/<family>/{diff,nor,rough}.jpg` (credits + prep recipe:
 * that folder's README).
 *
 * Two kinds of family:
 *  - TINT families (walls, made ground, trim, roofs, metal): the diffuse was
 *    prepped OFFLINE near-neutral (linear mean ~0.6) because every map surface
 *    is tinted by a per-box vertex colour (team colours, neon towers, painted
 *    houses). The scan supplies relief + grain, the tint keeps the palette.
 *  - NATURAL ground (grass / sand / dirt): the scan keeps its OWN colour and
 *    only lerps 45-60 % toward the box tint in the shader — a grey ground scan
 *    times a green tint read as felt.
 * Walls are routed by theme in renderer.familyMat (castle stone, day
 * sandstone block, neon concrete panels) via `userData.npPbrFamily`.
 * Every photo material also gets the MACRO anti-tiling layer (see FRAG_MAP).
 *
 * Lifecycle:
 *  - `startPbrLoad()` is called by the GameRenderer constructor (match / spectate
 *    only — no menu screen builds a GameRenderer, so menus never fetch these).
 *    Idempotent; module-cached forever (a second map load reuses everything).
 *  - Decode is ASYNC: `HTMLImageElement.decode()` runs off the main thread, so
 *    the only main-thread cost is the GPU upload, which renderer.precompile pays
 *    under the map load screen (renderer.precompile).
 *  - `upgradeMapMaterials(root)` swaps a procedural family's map/normal/rough
 *    for the photo set on every material in the map group. A family whose three
 *    files did not ALL load keeps its procedural canvas (per-family fallback);
 *    lowSpec / TV never start the load at all and stay fully procedural.
 *  - No mid-match swap: if the set is not ready when the load screen ends, that
 *    round keeps the canvas look and the next map load picks the photos up.
 */

export type PbrFamily =
  | "surface" | "plaster" | "plasterNeon" | "castle" | "wood" | "concrete" | "trim" | "paving" | "pavingNeon"
  | "bark" | "boulder" | "roofing" | "galv" | "grass" | "sand" | "dirt";

interface PbrSpec {
  /** Poly Haven asset id (credit / provenance). */
  id: string;
  /** real-world edge length of one texture tile, metres (Poly Haven `dimensions`). */
  meters: number;
  /** metres per UV unit at the dominant call site (worldProjectUV `tile`). */
  uvTile: number;
  /** UVs are WORLD-ANCHORED via uvAnchor (wrapped mod uvTile): the repeat must
   *  then be an INTEGER or a joint between two boxes shows a seam. */
  anchored: boolean;
  /** absolute normalScale for the photo normal map. Above 1 on purpose: a 1k
   *  scan's true relief is mip-filtered to almost nothing at FPS range. */
  normal: number;
  /** anisotropy wanted (clamped by the GPU cap / weak-GPU cap). */
  aniso: number;
  /** LINEAR mean albedo of diff.jpg (printed by the prep script) — the macro
   *  layer divides by it so the anti-tiling term is brightness-neutral. */
  mean: readonly [number, number, number];
  /** macro anti-tiling strength (0 = off). */
  macro: number;
  /** NATURAL ground: the scan keeps its own colour and only lerps this far
   *  toward the box tint (0 = tint family: grey scan x vertex tint). */
  natural: number;
}

export const PBR_SPECS: Readonly<Record<PbrFamily, PbrSpec>> = {
  // generic industrial walls (compound/reactor), street iron, bollards
  surface: { id: "metal_plate_02", meters: 2.0, uvTile: 3.2, anchored: true, normal: 1.3, aniso: 8, mean: [0.58, 0.58, 0.581], macro: 0.3, natural: 0 },
  // generic walls, DAY maps: sandstone block coursing reads at 5-15 m
  plaster: { id: "white_sandstone_bricks", meters: 2.0, uvTile: 3.2, anchored: true, normal: 1.4, aniso: 8, mean: [0.622, 0.622, 0.621], macro: 0.35, natural: 0 },
  // generic walls, NEON maps: cast concrete panels with formwork seams
  plasterNeon: { id: "concrete_wall_004", meters: 2.0, uvTile: 3.2, anchored: true, normal: 1.4, aniso: 8, mean: [0.611, 0.612, 0.612], macro: 0.35, natural: 0 },
  // generic walls on the medieval castle: rubble stone
  castle: { id: "castle_wall_varriation", meters: 2.0, uvTile: 3.2, anchored: true, normal: 1.5, aniso: 8, mean: [0.566, 0.565, 0.563], macro: 0.35, natural: 0 },
  // sawn boards on the timber maps (ship hull, western town)
  wood: { id: "weathered_brown_planks", meters: 1.8, uvTile: 3.2, anchored: true, normal: 1.3, aniso: 8, mean: [0.596, 0.557, 0.511], macro: 0.3, natural: 0 },
  // made ground on neon/night maps
  concrete: { id: "concrete_floor_02", meters: 2.0, uvTile: 6, anchored: true, normal: 1.6, aniso: 16, mean: [0.574, 0.573, 0.571], macro: 0.55, natural: 0 },
  // cornices / plinths / caps — cast-in-place concrete with form joints
  trim: { id: "concrete_wall_008", meters: 2.71, uvTile: 2.8, anchored: true, normal: 1.3, aniso: 8, mean: [0.633, 0.632, 0.634], macro: 0.3, natural: 0 },
  // streets / plazas / decks
  paving: { id: "concrete_pavement_02", meters: 1.8, uvTile: 5.5, anchored: true, normal: 2.0, aniso: 16, mean: [0.591, 0.59, 0.59], macro: 0.5, natural: 0 },
  // streets / plazas on NEON maps: large slabs, glossier (catches the neon)
  pavingNeon: { id: "concrete_tiles_02", meters: 1.825, uvTile: 5.5, anchored: true, normal: 1.6, aniso: 16, mean: [0.592, 0.592, 0.593], macro: 0.5, natural: 0 },
  // tree trunks (decor, box-local UVs at 0.5 m)
  bark: { id: "bark_brown_02", meters: 1.0, uvTile: 0.5, anchored: false, normal: 1.3, aniso: 8, mean: [0.534, 0.513, 0.454], macro: 0, natural: 0 },
  // boulders (decor, 0.7 m)
  boulder: { id: "rock_face", meters: 2.38, uvTile: 0.7, anchored: false, normal: 1.3, aniso: 8, mean: [0.574, 0.574, 0.573], macro: 0, natural: 0 },
  // flat roofs: ballast gravel over membrane
  roofing: { id: "gravel_concrete", meters: 2.12, uvTile: 3.4, anchored: true, normal: 1.3, aniso: 16, mean: [0.573, 0.573, 0.573], macro: 0.45, natural: 0 },
  // rooftop plant: corrugated galvanised sheet (1.1 m)
  galv: { id: "corrugated_iron", meters: 1.12, uvTile: 1.1, anchored: false, normal: 1.2, aniso: 8, mean: [0.61, 0.61, 0.61], macro: 0, natural: 0 },
  // NATURAL day ground, chosen per box by its tint (renderer groundClass)
  grass: { id: "forest_leaves_02", meters: 3.0, uvTile: 6, anchored: true, normal: 1.8, aniso: 16, mean: [0.117, 0.126, 0.013], macro: 1.0, natural: 0.45 },
  sand: { id: "rocky_trail", meters: 2.0, uvTile: 6, anchored: true, normal: 1.8, aniso: 16, mean: [0.223, 0.149, 0.081], macro: 1.0, natural: 0.6 },
  dirt: { id: "forest_ground_04", meters: 3.15, uvTile: 6, anchored: true, normal: 1.8, aniso: 16, mean: [0.151, 0.107, 0.063], macro: 1.0, natural: 0.45 },
};

/** Natural-ground class of a box tint (only called for boxes naturalGround()
 *  already accepted): green hues are grass, light warm hues sand, dark dirt. */
export function groundClass(colour: string | undefined): "grass" | "sand" | "dirt" {
  const c = new THREE.Color(colour ?? "#808080");
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl, THREE.SRGBColorSpace);
  const hue = hsl.h * 360;
  if (hue >= 70 && hue <= 175) return "grass";
  return hsl.l >= 0.45 ? "sand" : "dirt";
}

export const PBR_FAMILIES = Object.keys(PBR_SPECS) as PbrFamily[];
export const PBR_BASE = "/textures/pbr";

/** Texture repeat that makes one photo tile ≈ its real size. Anchored families
 *  round to an integer >= 1 (seam-free across world-anchored box joints). */
export function pbrRepeat(s: PbrSpec): number {
  const r = s.uvTile / s.meters;
  return s.anchored ? Math.max(1, Math.round(r)) : r;
}

export interface PbrSet {
  map: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
  normalScale: THREE.Vector2;
  /** shared per-family uniform objects (one set per family, never per frame). */
  uniforms: { uNpMean: { value: THREE.Vector3 }; uNpMacroAmt: { value: number }; uNpNatural: { value: number } };
}

/* ---------------------------------------------------------------------------
 * PHOTO SHADER LAYER (one program variant, key PBR_PROGRAM_KEY, values are
 * uniforms so every photo family shares it):
 *  - MACRO anti-tiling: the SAME albedo is sampled a second time at 1/9 the
 *    frequency, rotated, with a +3 mip bias, and its ratio to the texture mean modulates
 *    the detail sample. At 20-50 m, where the detail is mip-filtered flat, the
 *    ground still carries large soft patches made of its own material — and the
 *    1.5-3 m repeat stops reading as a grid. One extra texture fetch, on every
 *    tier that gets photos (mapWeather's macro term is off on iGPUs).
 *  - NATURAL ground: the vertex colour (the box tint — flat ground bakes no AO)
 *    is divided back out, and the scan's own colour is only lerped
 *    `uNpNatural` toward the tint's HUE at the scan's luminance (brightness
 *    always stays the photo's).
 * Patched AFTER any existing onBeforeCompile (mapWeather / wind), whose anchors
 * this leaves intact.
 * ------------------------------------------------------------------------- */
export const PBR_PROGRAM_KEY = "npPbr1";
export const PBR_MACRO_SCALE = 1 / 9;
const FRAG_DECL = /* glsl */ `
uniform vec3 uNpMean;
uniform float uNpMacroAmt;
uniform float uNpNatural;
`;
const FRAG_MAP = /* glsl */ `
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D( map, vMapUv );
  if ( uNpMacroAmt > 0.0 ) {
    // rotated ~37 deg so the macro grid never lines up with the detail repeat
    vec2 npMuv = mat2( 0.8, -0.6, 0.6, 0.8 ) * vMapUv * ${PBR_MACRO_SCALE.toFixed(6)} + vec2( 0.37, 0.61 );
    vec3 npMac = texture2D( map, npMuv, 3.0 ).rgb;
    sampledDiffuseColor.rgb *= mix( vec3( 1.0 ), clamp( npMac / max( uNpMean, vec3( 1e-3 ) ), 0.4, 1.9 ), uNpMacroAmt );
  }
  diffuseColor *= sampledDiffuseColor;
#endif
`;
const FRAG_NATURAL = /* glsl */ `
#if defined( USE_COLOR ) || defined( USE_COLOR_ALPHA )
if ( uNpNatural > 0.0 ) {
  const vec3 npW = vec3( 0.2126, 0.7152, 0.0722 );
  vec3 npT = diffuseColor.rgb / max( vColor.rgb, vec3( 1e-3 ) );
  // the tint's HUE at the scan's own luminance: brightness stays the photo's
  // (a bright tint used as an albedo washed sand out to white)
  vec3 npTint = vColor.rgb / max( dot( vColor.rgb, npW ), 1e-3 ) * dot( npT, npW );
  diffuseColor.rgb = mix( npT, npTint, uNpNatural );
}
#endif
`;
export const PBR_ANCHORS = [
  ["#include <common>", "common"],
  ["#include <map_fragment>", "map"],
  ["#include <color_fragment>", "color"],
] as const;

/** Pure string patch (bench-tested). Null (= stock shader) if an anchor is missing. */
export function patchPbrFragment(fs: string): string | null {
  for (const [a] of PBR_ANCHORS) if (fs.split(a).length !== 2) return null;
  return fs
    .replace("#include <common>", `#include <common>\n${FRAG_DECL}`)
    .replace("#include <map_fragment>", FRAG_MAP)
    .replace("#include <color_fragment>", `#include <color_fragment>\n${FRAG_NATURAL}`);
}

function applyPbrShader(mat: THREE.MeshStandardMaterial, u: PbrSet["uniforms"]): void {
  if (mat.userData.npPbrShader) return;
  mat.userData.npPbrShader = true;
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey();
  mat.onBeforeCompile = function (shader, renderer) {
    prev.call(this, shader, renderer);
    const fs = patchPbrFragment(shader.fragmentShader);
    if (!fs) return;
    shader.fragmentShader = fs;
    Object.assign(shader.uniforms, u);
  };
  mat.customProgramCacheKey = () => `${prevKey}|${PBR_PROGRAM_KEY}`;
  mat.needsUpdate = true;
}

/** Facade underlay: geometry of the procedural facade's glass panes, mirrored
 *  from mapTextures.makeFacade (512 px canvas, 4x4 cells). Exported there as
 *  FACADE_CELLS / FACADE_GLASS; duplicated here only as types to keep this
 *  module free of mapTextures' canvas makers. */
export interface FacadeLayout { cells: readonly number[]; glass: { x: number; y: number; w: number; h: number }; size: number }

const sets = new Map<PbrFamily, PbrSet>();
let facadeImg: HTMLImageElement | null = null;
let facadeTex: THREE.CanvasTexture | null = null;
let loadPromise: Promise<void> | null = null;
let failed = 0;

/** test seam: swap the image fetch (bench harness / headless checks). */
export let pbrImageLoader = (url: string): Promise<HTMLImageElement | ImageBitmap> => {
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  // decode() resolves once the bitmap is decoded OFF the main thread, so the
  // later texImage2D does not pay a synchronous JPEG decode.
  return img.decode().then(() => img);
};
export function setPbrImageLoader(fn: typeof pbrImageLoader): void { pbrImageLoader = fn; }

function mkTex(img: HTMLImageElement | ImageBitmap, srgb: boolean, repeat: number, aniso: number): THREE.Texture {
  const t = new THREE.Texture(img as HTMLImageElement);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.anisotropy = aniso;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.userData.shared = true;   // module-cached: map teardown must never dispose
  t.userData.npPbr = true;
  t.needsUpdate = true;
  return t;
}

/** Kick off the photo-texture load. Idempotent. `enabled=false` (lowSpec / TV)
 *  resolves immediately with nothing loaded — those machines stay procedural. */
export function startPbrLoad(opts: { enabled: boolean; maxAnisotropy: number }): Promise<void> {
  if (loadPromise) return loadPromise;
  if (!opts.enabled) { loadPromise = Promise.resolve(); return loadPromise; }
  const t0 = performance.now();
  const one = async (fam: PbrFamily): Promise<void> => {
    const s = PBR_SPECS[fam];
    const rep = pbrRepeat(s);
    const aniso = Math.max(1, Math.min(s.aniso, opts.maxAnisotropy));
    try {
      const [d, n, r] = await Promise.all(
        (["diff", "nor", "rough"] as const).map((k) => pbrImageLoader(`${PBR_BASE}/${fam}/${k}.jpg`)),
      );
      sets.set(fam, {
        map: mkTex(d, true, rep, aniso),
        normal: mkTex(n, false, rep, aniso),
        roughness: mkTex(r, false, rep, Math.min(aniso, 4)),
        normalScale: new THREE.Vector2(s.normal, s.normal),
        uniforms: {
          uNpMean: { value: new THREE.Vector3(s.mean[0], s.mean[1], s.mean[2]) },
          uNpMacroAmt: { value: s.macro },
          uNpNatural: { value: s.natural },
        },
      });
    } catch {
      failed++; // per-family fallback: this family keeps its canvas texture
    }
  };
  const facade = pbrImageLoader(`${PBR_BASE}/facade/diff.jpg`)
    .then((img) => { facadeImg = img as HTMLImageElement; })
    .catch(() => { failed++; });
  loadPromise = Promise.all([...PBR_FAMILIES.map(one), facade]).then(() => {
    console.log(`[pbr] ${sets.size}/${PBR_FAMILIES.length} photo families${facadeImg ? " + facade" : ""} in ${(performance.now() - t0).toFixed(0)} ms${failed ? ` (${failed} failed -> procedural)` : ""}`);
  });
  return loadPromise;
}

/** Wait (bounded) for the load. Resolves true when it finished in time. */
export function pbrReady(timeoutMs: number): Promise<boolean> {
  if (!loadPromise) return Promise.resolve(false);
  let done = false;
  const p = loadPromise.then(() => { done = true; return true; });
  return Promise.race([p, new Promise<boolean>((res) => window.setTimeout(() => res(done), timeoutMs))]);
}

/** The raw load promise, no deadline (offline screenshot harness only — its
 *  virtual clock fast-forwards every setTimeout-based wait). */
export function pbrLoadPromise(): Promise<void> | null { return loadPromise; }

export function pbrSet(fam: string): PbrSet | null {
  return sets.get(fam as PbrFamily) ?? null;
}

/** Every loaded photo texture (for the load-screen GPU upload). */
export function pbrTextures(): THREE.Texture[] {
  const out: THREE.Texture[] = [];
  for (const s of sets.values()) out.push(s.map, s.normal, s.roughness);
  if (facadeTex) out.push(facadeTex);
  return out;
}

/** The facade keeps its procedural window grid (glow map is paired with it):
 *  composite the photo plaster UNDER the canvas albedo (multiply), then restore
 *  the glass panes untextured. Built once, cached. */
function facadeComposite(proc: THREE.Texture, layout: FacadeLayout): THREE.CanvasTexture | null {
  if (facadeTex) return facadeTex;
  if (!facadeImg || typeof document === "undefined") return null;
  const src = proc.image as HTMLCanvasElement | undefined;
  if (!src || !(src as HTMLCanvasElement).getContext) return null;
  const S = 1024, k = S / layout.size;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d");
  if (!g) return null;
  // one facade UV repeat = 4.5 m; the plaster scan is 2 m -> 2x2 tiles (2.25 m)
  const half = S / 2;
  for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) g.drawImage(facadeImg, x * half, y * half, half, half);
  g.globalCompositeOperation = "multiply";
  g.drawImage(src, 0, 0, S, S);
  g.globalCompositeOperation = "source-over";
  const gl = layout.glass;
  for (const cx of layout.cells) for (const cy of layout.cells) {
    g.drawImage(src, cx + gl.x, cy + gl.y, gl.w, gl.h, (cx + gl.x) * k, (cy + gl.y) * k, gl.w * k, gl.h * k);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = proc.anisotropy;
  t.userData.shared = true;
  t.userData.npPbr = true;
  facadeTex = t;
  return t;
}

/** Swap procedural family textures for the photo set on every standard material
 *  under `root`. Load-time only (traverse allocates). Materials opt out with
 *  `userData.npNoPbr` (Minecraft imports keep their voxel look). Returns the
 *  number of materials upgraded. */
export function upgradeMapMaterials(root: THREE.Object3D, facadeLayout: FacadeLayout): number {
  if (sets.size === 0 && !facadeImg) return 0;
  let n = 0;
  const seen = new Set<THREE.Material>();
  root.traverse((o) => {
    const m = (o as THREE.Mesh).material;
    if (!m) return;
    for (const mat of Array.isArray(m) ? m : [m]) {
      if (seen.has(mat)) continue;
      seen.add(mat);
      if (upgradeMaterial(mat as THREE.MeshStandardMaterial, facadeLayout)) n++;
    }
  });
  return n;
}

export function upgradeMaterial(mat: THREE.MeshStandardMaterial, facadeLayout: FacadeLayout): boolean {
  if (!(mat as { isMeshStandardMaterial?: boolean }).isMeshStandardMaterial || mat.userData.npNoPbr) return false;
  // renderer may route a material to a specific photo family (theme walls,
  // natural-ground class); otherwise the procedural family of its albedo
  const fam = (mat.userData.npPbrFamily as string | undefined) ?? (mat.map?.userData.npFamily as string | undefined);
  if (!fam || !mat.map || mat.map.userData.npPbr) return false;
  if (fam === "facade") {
    const t = facadeComposite(mat.map!, facadeLayout);
    if (!t) return false;
    mat.map = t; // normal/rough/emissive stay procedural: they ARE the window grid
    return true;
  }
  const s = sets.get(fam as PbrFamily);
  if (!s) return false;
  const hadN = !!mat.normalMap, hadR = !!mat.roughnessMap;
  mat.map = s.map;
  mat.normalMap = s.normal;
  mat.normalScale = s.normalScale;
  mat.roughnessMap = s.roughness;
  // a newly-present map slot changes the program defines
  if (!hadN || !hadR) mat.needsUpdate = true;
  applyPbrShader(mat, s.uniforms);
  return true;
}

/** test seam: forget everything (bench only). */
export function _resetPbrForTest(): void {
  sets.clear(); facadeImg = null; facadeTex = null; loadPromise = null; failed = 0;
}
